// type ColorShade = 100 | 200 | 300 | 400 | 50 | 500 | 600 | 700 | 800 | 900 | 950;

// import { safeParseInt } from '@frontend/duck-tape';
import type { ValueOf } from 'type-fest';

const blue = {
  100: '#e3f3fd',
  200: '#bfe4f8',
  300: '#9bd5f3',
  400: '#55bdea',
  50: '#f1f9fe',
  500: '#36a8d1',
  600: '#1280b7',
  700: '#0f6b98',
  800: '#11577b',
  900: '#0d445f',
  950: '#072838',
} as const;
const cyan = {
  100: '#cff9fe',
  200: '#a5f2fc',
  300: '#67e8f9',
  400: '#22d6ee',
  50: '#ecfdff',
  500: '#06bcd4',
  600: '#089eb2',
  700: '#0e8190',
  800: '#156a75',
  900: '#165a63',
  950: '#083d44',
} as const;

const green = {
  100: '#e3f9e8',
  200: '#bff3d1',
  300: '#9bedba',
  400: '#73db99',
  50: '#f1fcf4',
  500: '#4ec77a',
  600: '#20994d',
  700: '#1a7d3e',
  800: '#1c5f35',
  900: '#15432a',
  950: '#0a2415',
} as const;

const mint = {
  100: '#e9f7f1',
  200: '#d3efe3',
  300: '#bde7d5',
  400: '#a2dbc6',
  50: '#f4fbf8',
  500: '#87cfab',
  600: '#6bb490',
  700: '#559975',
  800: '#407e5a',
  900: '#2b633f',
  950: '#163824',
} as const;

const lime = {
  100: '#f8fdca',
  200: '#eefb86',
  300: '#e3f660',
  400: '#d0eb30',
  50: '#fdffe6',
  500: '#b2d111',
  600: '#8ba709',
  700: '#687f0c',
  800: '#536410',
  900: '#465512',
  950: '#242f04',
} as const;

const navy = {
  100: '#e8edf6',
  200: '#ccd8eb',
  300: '#9fb9da',
  400: '#6c94c4',
  50: '#f4f6fb',
  500: '#4977ae',
  600: '#375d92',
  700: '#2e4c76',
  800: '#294163',
  900: '#23334c',
  950: '#1a2437',
} as const;

const neutral = {
  0: '#ffffff',
  100: '#f0f0f0',
  200: '#e7e7e7',
  300: '#d1d1d1',
  400: '#b0b0b0',
  50: '#fafafa',
  500: '#9c9c9c',
  600: '#888888',
  700: '#7d7d7d',
  800: '#696969',
  900: '#3d3d3d',
  950: '#292929',
} as const;

const pink = {
  100: '#f9eaf0',
  200: '#f4d6e1',
  300: '#e9a7be',
  400: '#e086a3',
  50: '#fbf4f7',
  500: '#d26283',
  600: '#bf4362',
  700: '#a4324b',
  800: '#882c3f',
  900: '#722938',
  950: '#45121c',
} as const;

const red = {
  100: '#fee5e5',
  200: '#fdcbcb',
  300: '#fba8a8',
  400: '#f45e5e',
  50: '#fef2f2',
  500: '#e74040',
  600: '#d92929',
  700: '#b52222',
  800: '#971d1d',
  900: '#6b1515',
  950: '#3a0b0b',
} as const;

const orange = {
  100: '#fdeedd',
  200: '#f9d6af',
  300: '#f5be81',
  400: '#ef9248',
  50: '#fef7ee',
  500: '#e37730',
  600: '#dd5b19',
  700: '#b94a15',
  800: '#92371a',
  900: '#6b2915',
  950: '#3a160b',
} as const;

const rose = {
  100: '#fceae4',
  200: '#fad9ce',
  300: '#f4af99',
  400: '#ef967a',
  50: '#fdf5f3',
  500: '#e37450',
  600: '#cf5933',
  700: '#ae4827',
  800: '#903e24',
  900: '#783824',
  950: '#411a0e',
} as const;

const slate = {
  100: '#ebeff3',
  200: '#d3dce4',
  300: '#adbecc',
  400: '#819baf',
  50: '#f6f8f9',
  500: '#617f96',
  600: '#486075',
  700: '#3f5365',
  800: '#374755',
  900: '#313d49',
  950: '#212830',
} as const;

const teal = {
  100: '#d5f7f8',
  200: '#b1edf0',
  300: '#8ee3e9',
  400: '#3ec6d2',
  50: '#effcfb',
  500: '#22aab8',
  600: '#1f899b',
  700: '#206f7e',
  800: '#225b68',
  900: '#204c59',
  950: '#10313c',
} as const;

const violet = {
  100: '#f4eef9',
  200: '#ecdff5',
  300: '#d9bfea',
  400: '#c7a1df',
  50: '#fbf7fd',
  500: '#b17cd0',
  600: '#9c5fbe',
  700: '#864ba5',
  800: '#704188',
  900: '#5b366d',
  950: '#3e1d4e',
} as const;

const yellow = {
  100: '#f5edd0',
  200: '#e8d594',
  300: '#dfc16a',
  400: '#d7ab48',
  50: '#fcf9ee',
  500: '#ce9032',
  600: '#b67129',
  700: '#985425',
  800: '#7c4324',
  900: '#673820',
  950: '#3a1c0e',
} as const;

const brand = {
  100: '#fcfde8',
  200: '#f8fbcd',
  300: '#f3f9a9',
  400: '#eefb86',
  50: '#fefef4',
  500: '#dde76d',
  600: '#c8cc54',
  700: '#a7a53c',
  800: '#7e7c2d',
  900: '#55531e',
  950: '#2c2a0f',
} as const;

const cool = {
  100: '#e5edf7',
  200: '#d5e1ef',
  300: '#c5d5e7',
  400: '#b5c9df',
  50: '#f5f9ff',
  500: '#a5bdd7',
  600: '#8aa5c3',
  700: '#6f8daf',
  800: '#5a748c',
  900: '#455b69',
  950: '#2d3d46',
} as const;

const cream = {
  100: '#f4f4ef',
  200: '#ecebe7',
  300: '#ebe9e3',
  400: '#e6e4de',
  50: '#f9f9f7',
  500: '#d7d5cf',
  600: '#cac8c2',
  700: '#bdb9b3',
  800: '#9e9a94',
  900: '#7f7b75',
  950: '#4a4845',
} as const;

const colorPalettes = {
  blue,
  brand,
  cool,
  cream,
  cyan,
  green,
  lime,
  mint,
  navy,
  neutral,
  orange,
  pink,
  red,
  rose,
  slate,
  teal,
  violet,
  yellow,
};

export type PaletteColors = keyof typeof colorPalettes;

const standaloneColors = {
  // Credit selection screen colors
  buttonDark: '#262628',
  greyBlue: '#6383a1',
  lineGray: '#c7c7c7',
  // Additional colors from Figma designs
  linkBlue: '#0c67af',
  transparent: 'transparent',
  // This is meant to replace our current usage of "white", which isn't actually a pure white color.
  // When we do our design refactor we'll dedupe
  trueWhite: '#FFFFFF',
  white: '#FDFDFD',
} as const;

export const colorAliases = {
  borderError: red[600],
  borderLine: standaloneColors.lineGray,
  borderPrimary: neutral[950],
  borderSecondary: neutral[950],
  borderSuccess: green[900],
  borderTertiary: neutral[200],
  buttonAccent: lime[200],
  buttonDisabled: neutral[200],
  buttonError: red[100],
  buttonPrimary: neutral[950],
  buttonSecondary: neutral[50],
  buttonSuccess: green[200],
  surfaceAccent: lime[200],
  surfaceAccentPrimary: neutral[950],
  surfaceBrand: blue[950],
  surfaceCream: cream[400],
  surfaceError: red[200],
  surfacePink: pink[300],
  surfacePrimary: slate[100],
  surfaceSecondary: slate[50],
  surfaceSuccess: green[200],
  surfaceTertiary: slate[600],
  surfaceYellow: lime[200], // Most popular badge background
  textAccent: lime[200],
  textError: red[600],
  textErrorDisabled: red[200],
  textGreyBlue: standaloneColors.greyBlue,
  textInverse: neutral[50],
  // Additional aliases for payment/credit flow designs
  textLink: standaloneColors.linkBlue,
  textPink: pink[300],
  textPrimary: neutral[950],
  textSecondary: neutral[800],
  textSuccess: green[900],
  textSuccessDisabled: green[400],
  textTertiary: neutral[500],
} as const;

export const colors = {
  ...colorPalettes,
  ...standaloneColors,
  ...colorAliases,
};

export type ColorValue =
  | ValueOf<typeof colorAliases>
  | ValueOf<typeof standaloneColors>
  | ValueOf<ValueOf<typeof colorPalettes>>;

export type ColorName =
  | keyof typeof colorAliases
  | keyof typeof standaloneColors
  | `${keyof typeof colorPalettes}${keyof typeof blue}`; // Assuming all palettes have the same shades

export const resolveColorNameToColorValue = (colorProp: ColorName): ColorValue => {
  // Check if the colorProp is in standaloneColors or colorAliases
  if (Object.keys(standaloneColors).includes(colorProp)) {
    return standaloneColors[colorProp as keyof typeof standaloneColors];
  }
  if (Object.keys(colorAliases).includes(colorProp)) {
    return colorAliases[colorProp as keyof typeof colorAliases];
  }

  // Regex pattern to match "<colorName><colorShadeNumber>"
  const pattern = /^([a-z]+)(\d{2,3})$/;
  const match = colorProp.match(pattern);

  if (!match) {
    throw new Error(`Invalid colorProp format: ${colorProp}`);
  }

  const [_, colorName, shadeStr] = match;
  const shade = parseInt(shadeStr!, 10) as keyof typeof blue; // Safely parse the shade number

  if (!Object.keys(colorPalettes).includes(colorName!) || !(shade in blue)) {
    throw new Error(`Invalid color name or shade: ${colorName}${shade}`);
  }

  // Return the color value from the colorPalettes
  return colorPalettes[colorName as keyof typeof colorPalettes][shade];
};
